import { getItem, getMonster, getNpc, RARITY_INFO, type Rarity } from '@jianghu/game-data';
import { ENTITY_STATE, type EntitySnapshot, type EntitySpawnDTO } from '@jianghu/protocol';
import { lerpAngle, dampAngle } from '@jianghu/shared';
import * as THREE from 'three';
import { AnimationController } from '../animation/controller';
import { humanoidProfile, WOLF_PROFILE } from '../animation/profiles';
import { HumanoidRig } from './humanoid';
import { banditLook, npcLook, playerLook } from './look';
import { WolfRig } from './wolf';

interface Sample {
  t: number;
  x: number;
  z: number;
  yaw: number;
}

const proxyGeo = new THREE.CylinderGeometry(0.55, 0.55, 1.9, 8);
proxyGeo.translate(0, 0.95, 0);
const proxyMat = new THREE.MeshBasicMaterial({ visible: false });

/** Render-side representation of one networked entity: model, animation, interpolation. */
export class EntityView {
  readonly root = new THREE.Group();
  dto: EntitySpawnDTO;
  humanoid: HumanoidRig | null = null;
  wolf: WolfRig | null = null;
  anim: AnimationController | null = null;
  private lootModel: THREE.Object3D | null = null;
  private lootBeam: THREE.Mesh | null = null;
  private readonly buffer: Sample[] = [];
  readonly pos = new THREE.Vector3();
  yaw = 0;
  private renderYaw = 0;
  speed = 0;
  hp = 0;
  maxHp = 1;
  state: number = ENTITY_STATE.idle;
  statusBits = 0;
  dead = false;
  /** Seconds remaining of the fade-out after despawn (null = alive in the world). */
  fading: number | null = null;
  readonly proxy: THREE.Mesh;
  private t = 0;
  isLocal = false;

  get id(): number {
    return this.dto.id;
  }

  constructor(dto: EntitySpawnDTO) {
    this.dto = dto;
    this.root.name = `entity:${dto.id}`;
    this.pos.set(dto.x, 0, dto.z);
    this.yaw = dto.yaw;
    this.renderYaw = dto.yaw;
    this.hp = dto.hp ?? 0;
    this.maxHp = dto.maxHp ?? 1;
    this.proxy = new THREE.Mesh(proxyGeo, proxyMat);
    this.proxy.userData.entityId = dto.id;
    this.root.add(this.proxy);
    this.buildModel();
    if (dto.state === ENTITY_STATE.dead) this.setDead(true, true);
  }

  private buildModel(): void {
    const d = this.dto;
    if (d.k === 'player' && d.player) {
      this.humanoid = new HumanoidRig(
        playerLook(d.player.sect, d.player.appearance, d.player.weapon, d.player.armor),
      );
      this.anim = new AnimationController(humanoidProfile('sword'));
    } else if (d.k === 'monster') {
      const def = d.monsterId ? getMonster(d.monsterId) : undefined;
      if (def?.model === 'wolf') {
        this.wolf = new WolfRig(def.colors.body, def.colors.accent, def.scale);
        this.anim = new AnimationController(WOLF_PROFILE);
      } else {
        this.humanoid = new HumanoidRig(banditLook(d.id));
        this.anim = new AnimationController(humanoidProfile('sword'));
        if (def) this.humanoid.root.scale.setScalar(def.scale);
      }
      this.proxy.scale.set(1.1, def?.model === 'wolf' ? 0.6 : 1, 1.1);
    } else if (d.k === 'npc') {
      const def = d.npcId ? getNpc(d.npcId) : undefined;
      if (def) {
        this.humanoid = new HumanoidRig(npcLook(def.visual));
        const prop = def.visual.prop;
        this.anim = new AnimationController(
          humanoidProfile(
            prop === 'staff'
              ? 'staff'
              : prop === 'spear'
                ? 'spear'
                : prop === 'basket'
                  ? 'basket'
                  : prop === 'hammer'
                    ? 'sword'
                    : 'none',
          ),
        );
      }
    } else if (d.k === 'loot') {
      this.buildLoot();
    }
    if (this.humanoid) this.root.add(this.humanoid.root);
    if (this.wolf) this.root.add(this.wolf.root);
  }

  private buildLoot(): void {
    const rarity = (this.dto.loot?.rarity ?? 'common') as Rarity;
    const color = RARITY_INFO[rarity]?.color ?? '#ffffff';
    const g = new THREE.Group();
    const sack = new THREE.Mesh(
      new THREE.SphereGeometry(0.28, 12, 10),
      new THREE.MeshStandardMaterial({ color: '#b08a52', roughness: 0.9 }),
    );
    sack.scale.set(1, 0.85, 1);
    sack.position.y = 0.24;
    sack.castShadow = true;
    const tie = new THREE.Mesh(
      new THREE.ConeGeometry(0.12, 0.2, 8),
      new THREE.MeshStandardMaterial({ color: '#8a6a3a' }),
    );
    tie.position.y = 0.52;
    g.add(sack, tie);
    const beam = new THREE.Mesh(
      new THREE.CylinderGeometry(0.12, 0.3, 3.2, 12, 1, true),
      new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.35,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    );
    beam.position.y = 1.6;
    g.add(beam);
    this.lootBeam = beam;
    this.lootModel = g;
    this.root.add(g);
    this.proxy.scale.set(1, 0.4, 1);
  }

  /** Re-applies an updated spawn DTO (e.g. equipment change). */
  refresh(dto: EntitySpawnDTO): void {
    const prev = this.dto;
    this.dto = dto;
    if (dto.k === 'player' && dto.player && prev.player) {
      const changed = prev.player.weapon !== dto.player.weapon || prev.player.armor !== dto.player.armor;
      if (changed && this.humanoid) {
        this.root.remove(this.humanoid.root);
        this.humanoid.dispose();
        this.humanoid = new HumanoidRig(
          playerLook(dto.player.sect, dto.player.appearance, dto.player.weapon, dto.player.armor),
        );
        this.root.add(this.humanoid.root);
      }
    }
    if (dto.hp !== undefined) this.hp = dto.hp;
    if (dto.maxHp !== undefined) this.maxHp = dto.maxHp;
  }

  pushSnapshot(t: number, s: EntitySnapshot): void {
    const [, x, z, yaw, hp, state, bits] = s;
    this.buffer.push({ t, x, z, yaw });
    if (this.buffer.length > 30) this.buffer.splice(0, this.buffer.length - 30);
    this.hp = hp;
    this.statusBits = bits;
    if (state !== this.state) {
      this.state = state;
      if (this.anim) this.anim.stunned = state === ENTITY_STATE.stunned;
      if (state === ENTITY_STATE.dead && !this.dead) this.setDead(true, false);
      if (state !== ENTITY_STATE.dead && this.dead && this.dto.k === 'player') this.setDead(false, false);
    }
  }

  /** Remote entities: interpolate the snapshot buffer at `renderTime` (server clock, ms). */
  private sample(renderTime: number): Sample | null {
    const b = this.buffer;
    if (b.length === 0) return null;
    if (renderTime <= (b[0] as Sample).t) return b[0] as Sample;
    for (let i = b.length - 1; i >= 0; i--) {
      const a = b[i] as Sample;
      if (a.t <= renderTime) {
        const n = b[i + 1];
        if (!n) {
          // Brief extrapolation (≤150 ms) using the last velocity.
          const p = b[i - 1];
          if (!p) return a;
          const over = Math.min(150, renderTime - a.t);
          const k = over / Math.max(1, a.t - p.t);
          return { t: renderTime, x: a.x + (a.x - p.x) * k, z: a.z + (a.z - p.z) * k, yaw: a.yaw };
        }
        const u = (renderTime - a.t) / Math.max(1, n.t - a.t);
        return {
          t: renderTime,
          x: a.x + (n.x - a.x) * u,
          z: a.z + (n.z - a.z) * u,
          yaw: lerpAngle(a.yaw, n.yaw, u),
        };
      }
    }
    return b[0] as Sample;
  }

  /** Local player: position comes from client-side prediction. */
  setPredicted(x: number, z: number, yaw: number): void {
    this.pos.x = x;
    this.pos.z = z;
    this.yaw = yaw;
  }

  update(dt: number, renderTime: number, groundHeight: (x: number, z: number) => number): void {
    this.t += dt;
    const px = this.pos.x;
    const pz = this.pos.z;
    if (!this.isLocal) {
      const s = this.sample(renderTime);
      if (s) {
        this.pos.x = s.x;
        this.pos.z = s.z;
        this.yaw = s.yaw;
      }
    }
    const moved = Math.hypot(this.pos.x - px, this.pos.z - pz);
    const instSpeed = dt > 0 ? moved / dt : 0;
    this.speed += (Math.min(12, instSpeed) - this.speed) * (1 - Math.exp(-14 * dt));
    this.pos.y = groundHeight(this.pos.x, this.pos.z);
    this.renderYaw = dampAngle(this.renderYaw, this.yaw, 16, dt);
    this.root.position.copy(this.pos);
    this.root.rotation.y = this.renderYaw;

    if (this.anim) {
      const pose = this.anim.update(dt, this.dead ? 0 : this.speed);
      this.humanoid?.applyPose(pose);
      this.wolf?.applyPose(pose);
    }
    if (this.lootModel) {
      this.lootModel.position.y = 0.05 + Math.sin(this.t * 2.4) * 0.06;
      this.lootModel.rotation.y += dt * 0.6;
      if (this.lootBeam)
        (this.lootBeam.material as THREE.MeshBasicMaterial).opacity =
          (this.dto.loot?.mine === false ? 0.08 : 0.28) + Math.sin(this.t * 3) * 0.06;
    }
    if (this.fading !== null) {
      this.fading -= dt;
      const k = Math.max(0, this.fading / 0.6);
      this.root.scale.setScalar(0.6 + 0.4 * k);
      this.root.position.y -= (1 - k) * 0.6;
    }
  }

  playAction(name: string, durationSec: number): void {
    this.anim?.play(name, durationSec);
  }

  flinch(): void {
    if (!this.dead) this.anim?.flinch();
  }

  setDead(dead: boolean, instant: boolean): void {
    this.dead = dead;
    this.anim?.setDead(dead);
    if (dead && instant && this.anim) {
      // Jump straight to the end of the death clip for entities that were already dead on spawn.
      for (let i = 0; i < 40; i++) this.anim.update(0.05, 0);
    }
  }

  headHeight(): number {
    if (this.wolf) return 1.25;
    if (this.lootModel) return 0.9;
    return (this.humanoid?.height ?? 1.8) * (this.humanoid?.root.scale.y ?? 1) + 0.35;
  }

  headPos(out: THREE.Vector3): THREE.Vector3 {
    return out.set(this.pos.x, this.pos.y + this.headHeight(), this.pos.z);
  }

  chestPos(out: THREE.Vector3): THREE.Vector3 {
    return out.set(this.pos.x, this.pos.y + (this.wolf ? 0.7 : 1.15), this.pos.z);
  }

  weaponTip(out: THREE.Vector3): THREE.Vector3 | null {
    if (!this.humanoid) return null;
    this.root.updateMatrixWorld(true);
    return this.humanoid.bladeTip(out);
  }

  dispose(): void {
    this.humanoid?.dispose();
    this.wolf?.dispose();
    this.root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (m.isMesh && m !== this.proxy && this.lootModel) {
        m.geometry.dispose();
        (m.material as THREE.Material).dispose();
      }
    });
  }
}

export function itemModelName(defId: string | null): string | null {
  return defId ? (getItem(defId)?.model ?? null) : null;
}
